import { Injectable, inject } from '@angular/core';
import { SearchState, serializeSearchState, toFilterChips } from '@app/model';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import { BehaviorSubject } from 'rxjs';

/** A search the user ran: its URL state (first page) and a short label. */
export interface RecentSearch {
  state: string;
  label: string;
}

const STORAGE_KEY = 'hunter.recentSearches';
const LIMIT = 5;

/** The filter of a search in words, e.g. "Name: acme · Country: DE". */
export const searchLabel = (
  filterValue: unknown[],
  sectorNames: Map<number, string> = new Map()
) =>
  toFilterChips(filterValue, sectorNames)
    .map((chip) => `${chip.label}: ${chip.value}`)
    .join(' · ');

/**
 * The last searches of the search page with a filter, newest first, kept in
 * the local storage of the browser; a failing storage only loses them.
 */
@Injectable({ providedIn: 'root' })
export class RecentSearchesService {
  private storage = inject<Storage>(LOCAL_STORAGE);
  private recent = new BehaviorSubject<RecentSearch[]>(this.read());

  public readonly recent$ = this.recent.asObservable();

  /** Keeps a search; sectors are named by the given names. */
  public record(state: SearchState, sectorNames?: Map<number, string>) {
    if (!state.filterValue || !state.filterValue.length) {
      return;
    }
    const serialized = serializeSearchState(state);
    if (!serialized) {
      return;
    }
    const entry = {
      state: serialized,
      label: searchLabel(state.filterValue, sectorNames),
    };
    const recent = [
      entry,
      ...this.recent.value.filter((x) => x.state !== serialized),
    ].slice(0, LIMIT);
    this.recent.next(recent);
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(recent));
    } catch {
      // The searches live on in memory.
    }
  }

  private read(): RecentSearch[] {
    try {
      const value = JSON.parse(this.storage.getItem(STORAGE_KEY) ?? '[]');
      return Array.isArray(value)
        ? value
            .filter(
              (x): x is RecentSearch =>
                typeof x?.state === 'string' && typeof x?.label === 'string'
            )
            .slice(0, LIMIT)
        : [];
    } catch {
      return [];
    }
  }
}
